import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';
import axios, { INTERNAL_SERVER_ERROR, UNKNOWN_ERROR, endpoints } from 'src/utils/axios';
// @mui
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useSearchParams, useRouter } from 'src/routes/hooks';
// hooks
// auth
// components
import Iconify from 'src/components/iconify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { BackendValidationError } from 'src/utils/backend-validation-error';
import { ColoredIcon } from 'src/components/colored-icon';

// ----------------------------------------------------------------------

export default function ForgotPasswordView() {
  const router = useRouter();

  const [errorMsgs, setErrorMsgs] = useState<BackendValidationError[]>([]);

  const searchParams = useSearchParams();

  const returnTo = searchParams.get('returnTo');

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Email is required').email('Email must be a valid email address')
  });

  const defaultValues = {
    email: 'dusan.stanojevic.cs@gmail.com',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const [successfullyResetPassword, setSuccessfullyResetPassword] = useState(false);

  const onSubmit = handleSubmit(async (data) => {
    setErrorMsgs([])
    try {
      await axios.post(endpoints.users.forgotPassword, { email: data?.email })

      setSuccessfullyResetPassword(true);
    } catch (error) {
      console.log(error)
      setErrorMsgs(error?.response?.data || [{
        message:
          error?.response?.status === 500 ? INTERNAL_SERVER_ERROR : UNKNOWN_ERROR
      }]);
    }
  });

  const renderHead = (
    <Stack spacing={2} sx={{ mb: 5 }}>
      <Typography variant="h4" textAlign="center">Reset Password</Typography>
    </Stack>
  );

  const renderForm = (
    <Stack spacing={2.5}>
      {errorMsgs.length > 0 && errorMsgs.map((error, index) => <Alert key={index} severity="error">{error.message}</Alert>)}

      {successfullyResetPassword && <Box>
        <Stack direction="row" justifyContent="center">
          <ColoredIcon color="success">
            <Iconify icon="eva:checkmark-circle-2-fill" width={72} />
          </ColoredIcon>
        </Stack>

        <Typography component="h2" sx={{ textAlign: "center", mb: 4 }}>We&apos;ve sent you an email with a link to reset your password.</Typography>

        <LoadingButton
          fullWidth
          color="inherit"
          size="large"
          onClick={() => {
            router.push(paths.auth.login)
          }}
          variant="contained"
          loading={isSubmitting}
        >
          Back to Sign in
        </LoadingButton>
      </Box>}

      {!successfullyResetPassword && <>
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <Typography variant="body2">Back to </Typography>

          <Link component={RouterLink} href={paths.auth.login} variant="subtitle2">
            Sign in
          </Link>
        </Stack>

        <RHFTextField name="email" label="Email Address" />

        <LoadingButton
          fullWidth
          color="inherit"
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
        >
          Request Reset
        </LoadingButton>
      </>}
    </Stack>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      {renderHead}

      {renderForm}
    </FormProvider>
  );
}
